// Catálogo del taller: fallas, modelos y técnicos. Taller-Game → Catálogo.
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Switch } from '@/features/inventario/shared';
import {
  Campo, fieldCls, money, useSort, SortTh, PanelIntegrado,
  Catalogo, TIPOS_EQUIPO, tallerRpc, QK,
} from './shared';

type Pestana = 'fallas' | 'modelos' | 'tecnicos' | 'compatibilidad';
type Tabla = 'modelo' | 'falla' | 'tecnico';

const BANCOS: [string, string][] = [
  ['microsoldadura', 'Microsoldadura'], ['reballing', 'Reballing'], ['limpieza', 'Limpieza'],
  ['controles', 'Controles'], ['pc', 'PC'], ['gpu', 'GPU'],
];
const tipoLabel = (t: string) => TIPOS_EQUIPO.find(([v]) => v === t)?.[1] ?? t;

export function CatalogoTaller() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Catalogo>({ queryKey: [QK, 'catalogo'], queryFn: () => tallerRpc<Catalogo>('catalogo') });
  const [pestana, setPestana] = useState<Pestana>('fallas');

  const guardar = useMutation({
    mutationFn: (p: { tabla: Tabla; d: Record<string, unknown> }) => tallerRpc('catalogo_guardar', { _tabla: p.tabla, _d: p.d }),
    onSuccess: () => { toast.success('Guardado'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const TABS: { key: Pestana; label: string }[] = [
    { key: 'fallas', label: 'Fallas' }, { key: 'modelos', label: 'Modelos' }, { key: 'tecnicos', label: 'Técnicos' },
    { key: 'compatibilidad', label: 'Compatibilidad' },
  ];

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Wrench className="w-5 h-5" /> Catálogo del taller</h1>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button key={t.key} type="button"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${pestana === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setPestana(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {isLoading || !data ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div> : (
        <>
          {pestana === 'fallas' && <FallasTab data={data} guardar={guardar} />}
          {pestana === 'modelos' && <ModelosTab data={data} guardar={guardar} />}
          {pestana === 'tecnicos' && <TecnicosTab data={data} guardar={guardar} />}
          {pestana === 'compatibilidad' && <CompatibilidadTab data={data} />}
        </>
      )}
    </div>
  );
}

interface GuardarMutation {
  mutate: (p: { tabla: Tabla; d: Record<string, unknown> }, opts?: { onSuccess?: () => void }) => void;
  isPending: boolean;
}

// ───────────────────────────── Fallas ─────────────────────────────────────
function FallasTab({ data, guardar }: { data: Catalogo; guardar: GuardarMutation }) {
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [q, setQ] = useState('');
  const [editando, setEditando] = useState<Catalogo['fallas'][number] | 'nueva' | null>(null);

  const repuestoNombre = useCallback((id: string) => data.repuestos.find((r) => r.id === id)?.nombre ?? '(repuesto eliminado)', [data.repuestos]);

  const filtradas = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return data.fallas.filter((f) =>
      (!tipoFiltro || f.tipo === tipoFiltro) &&
      (!qq || [f.nombre, f.modelo, f.banco, tipoLabel(f.tipo)].filter(Boolean).some((v) => String(v).toLowerCase().includes(qq))));
  }, [data.fallas, tipoFiltro, q]);

  const getVal = useCallback((f: Catalogo['fallas'][number], key: string): string | number | null => {
    switch (key) {
      case 'tipo': return tipoLabel(f.tipo);
      case 'modelo': return f.modelo ?? '';
      case 'nombre': return f.nombre;
      case 'banco': return f.banco ?? '';
      case 'minutos': return f.minutos;
      case 'precio_mano_obra': return Number(f.precio_mano_obra);
      case 'garantia_dias': return f.garantia_dias;
      case 'activo': return f.activo ? 1 : 0;
      default: return null;
    }
  }, []);
  const sort = useSort(filtradas, getVal);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select className={`${fieldCls} w-48`} value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
          <option value="">Todos los tipos</option>
          {TIPOS_EQUIPO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input className={`${fieldCls} w-64`} placeholder="Buscar falla, modelo o banco…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="flex-1" />
        <Button size="sm" onClick={() => setEditando('nueva')}><Plus className="w-4 h-4 mr-1" /> Nueva falla</Button>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Fallas ({filtradas.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="tipo" sort={sort}>Tipo</SortTh>
              <SortTh col="modelo" sort={sort}>Modelo</SortTh>
              <SortTh col="nombre" sort={sort}>Falla</SortTh>
              <SortTh col="banco" sort={sort}>Banco</SortTh>
              <SortTh col="minutos" sort={sort} align="right">Minutos</SortTh>
              <SortTh col="precio_mano_obra" sort={sort} align="right">Mano de obra</SortTh>
              <SortTh col="garantia_dias" sort={sort} align="right">Garantía</SortTh>
              <th className="py-2 pr-3">Repuestos sugeridos</th>
              <SortTh col="activo" sort={sort}>Activa</SortTh>
              <th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">Sin fallas registradas</td></tr>}
              {sort.sorted.map((f, i) => (
                <tr key={f.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{tipoLabel(f.tipo)}</td>
                  <td className="py-2 pr-3">{f.modelo ?? <span className="text-muted-foreground">Todos</span>}</td>
                  <td className="py-2 pr-3">{f.nombre}</td>
                  <td className="py-2 pr-3">{f.banco ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{f.minutos}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.precio_mano_obra)}</td>
                  <td className="py-2 pr-3 text-right">{f.garantia_dias ?? '—'}</td>
                  <td className="py-2 pr-3 max-w-xs">
                    {f.repuestos.length === 0 ? '—' : f.repuestos.map((r) => `${r.cantidad}× ${repuestoNombre(r.producto_id)}`).join(', ')}
                  </td>
                  <td className="py-2 pr-3">{f.activo ? 'Activa' : 'Inactiva'}</td>
                  <td className="py-2 text-right">
                    <button type="button" className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEditando(f)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editando && (
        <PanelFalla falla={editando === 'nueva' ? null : editando} modelos={data.modelos} repuestos={data.repuestos}
          guardar={guardar} onClose={() => setEditando(null)} />
      )}
    </div>
  );
}

function PanelFalla({ falla, modelos, repuestos, guardar, onClose }: {
  falla: Catalogo['fallas'][number] | null; modelos: Catalogo['modelos']; repuestos: Catalogo['repuestos'];
  guardar: GuardarMutation; onClose: () => void;
}) {
  const [tipo, setTipo] = useState(falla?.tipo ?? TIPOS_EQUIPO[0][0]);
  const [modeloId, setModeloId] = useState(falla?.modelo_id ?? '');
  const [nombre, setNombre] = useState(falla?.nombre ?? '');
  const [sintomas, setSintomas] = useState(falla?.sintomas ?? '');
  const [causaProbable, setCausaProbable] = useState(falla?.causa_probable ?? '');
  const [procedimiento, setProcedimiento] = useState(falla?.procedimiento ?? '');
  const [banco, setBanco] = useState(falla?.banco ?? '');
  const [minutos, setMinutos] = useState(String(falla?.minutos ?? 60));
  const [precio, setPrecio] = useState(String(falla?.precio_mano_obra ?? 0));
  const [garantiaDias, setGarantiaDias] = useState(falla?.garantia_dias != null ? String(falla.garantia_dias) : '');
  const [activo, setActivo] = useState(falla?.activo ?? true);
  const [repSel, setRepSel] = useState<{ producto_id: string; cantidad: number }[]>(falla?.repuestos ?? []);
  const [repAgregar, setRepAgregar] = useState('');

  const modelosDelTipo = useMemo(() => modelos.filter((m) => m.tipo === tipo), [modelos, tipo]);

  const agregarRepuesto = () => {
    if (!repAgregar || repSel.some((r) => r.producto_id === repAgregar)) return;
    setRepSel([...repSel, { producto_id: repAgregar, cantidad: 1 }]);
    setRepAgregar('');
  };
  const quitarRepuesto = (id: string) => setRepSel(repSel.filter((r) => r.producto_id !== id));
  const cambiarCantidad = (id: string, cantidad: number) => setRepSel(repSel.map((r) => (r.producto_id === id ? { ...r, cantidad } : r)));

  const valido = nombre.trim().length > 0;

  const onGuardar = () => {
    guardar.mutate({
      tabla: 'falla',
      d: {
        id: falla?.id, tipo, modelo_id: modeloId || null, nombre: nombre.trim(), sintomas: sintomas.trim() || null,
        causa_probable: causaProbable.trim() || null, procedimiento: procedimiento.trim() || null, banco: banco || null,
        minutos: Number(minutos) || 60, precio_mano_obra: Number(precio) || 0,
        garantia_dias: garantiaDias === '' ? null : Number(garantiaDias),
        repuestos: repSel, activo,
      },
    }, { onSuccess: onClose });
  };

  return (
    <PanelIntegrado title={falla ? `Editar falla · ${falla.nombre}` : 'Nueva falla'} onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Campo label="Tipo de equipo *">
          <select className={fieldCls} value={tipo} onChange={(e) => { setTipo(e.target.value); setModeloId(''); }}>
            {TIPOS_EQUIPO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Campo>
        <Campo label="Modelo">
          <select className={fieldCls} value={modeloId} onChange={(e) => setModeloId(e.target.value)}>
            <option value="">— Aplica a todos los modelos de este tipo —</option>
            {modelosDelTipo.map((m) => <option key={m.id} value={m.id}>{m.marca} {m.modelo}{m.revision ? ` ${m.revision}` : ''}</option>)}
          </select>
        </Campo>
        <Campo label="Nombre de la falla *" className="col-span-2">
          <input className={fieldCls} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej. No carga la batería del control" />
        </Campo>
        <Campo label="Síntomas" className="col-span-2">
          <textarea className={`${fieldCls} h-16`} value={sintomas} onChange={(e) => setSintomas(e.target.value)} />
        </Campo>
        <Campo label="Causa probable" className="col-span-2">
          <textarea className={`${fieldCls} h-16`} value={causaProbable} onChange={(e) => setCausaProbable(e.target.value)} />
        </Campo>
        <Campo label="Procedimiento" className="col-span-2">
          <textarea className={`${fieldCls} h-20`} value={procedimiento} onChange={(e) => setProcedimiento(e.target.value)} />
        </Campo>
        <Campo label="Banco">
          <select className={fieldCls} value={banco} onChange={(e) => setBanco(e.target.value)}>
            <option value="">— Sin banco —</option>
            {BANCOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Campo>
        <Campo label="Tiempo estándar (minutos)">
          <input type="number" min="0" className={fieldCls} value={minutos} onChange={(e) => setMinutos(e.target.value)} />
        </Campo>
        <Campo label="Precio de mano de obra sin IVA ($)">
          <input type="number" min="0" step="0.01" className={fieldCls} value={precio} onChange={(e) => setPrecio(e.target.value)} />
        </Campo>
        <Campo label="Garantía (días)">
          <input type="number" min="0" className={fieldCls} value={garantiaDias} onChange={(e) => setGarantiaDias(e.target.value)} placeholder="Usa la garantía general del taller" />
        </Campo>

        <div className="col-span-2 space-y-2 rounded-md border p-3">
          <span className="text-xs font-medium text-muted-foreground">Repuestos sugeridos</span>
          <div className="flex items-center gap-2">
            <select className={fieldCls} value={repAgregar} onChange={(e) => setRepAgregar(e.target.value)}>
              <option value="">— Elegir repuesto —</option>
              {repuestos.filter((r) => !repSel.some((s) => s.producto_id === r.id)).map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
            </select>
            <Button type="button" size="sm" variant="outline" disabled={!repAgregar} onClick={agregarRepuesto}><Plus className="w-4 h-4" /></Button>
          </div>
          {repSel.length > 0 && (
            <ul className="space-y-1">
              {repSel.map((r) => {
                const rep = repuestos.find((x) => x.id === r.producto_id);
                return (
                  <li key={r.producto_id} className="flex items-center gap-2 text-sm">
                    <span className="flex-1 truncate">{rep ? `${rep.codigo} · ${rep.nombre}` : r.producto_id}</span>
                    <input type="number" min="1" className={`${fieldCls} w-20`} value={r.cantidad} onChange={(e) => cambiarCantidad(r.producto_id, Number(e.target.value) || 1)} />
                    <button type="button" className="text-destructive hover:opacity-80" onClick={() => quitarRepuesto(r.producto_id)}><Trash2 className="w-4 h-4" /></button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="col-span-2"><Switch checked={activo} onChange={setActivo} label="Falla activa" /></div>
      </div>
      <div className="flex justify-end gap-2 border-t pt-3">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button isLoading={guardar.isPending} disabled={!valido} onClick={onGuardar}>Guardar</Button>
      </div>
    </PanelIntegrado>
  );
}

// ───────────────────────────── Modelos ────────────────────────────────────
function ModelosTab({ data, guardar }: { data: Catalogo; guardar: GuardarMutation }) {
  const [editando, setEditando] = useState<Catalogo['modelos'][number] | 'nueva' | null>(null);
  const getVal = useCallback((m: Catalogo['modelos'][number], key: string): string | number | null => {
    switch (key) {
      case 'tipo': return tipoLabel(m.tipo);
      case 'marca': return m.marca;
      case 'modelo': return m.modelo;
      case 'revision': return m.revision ?? '';
      case 'activo': return m.activo ? 1 : 0;
      default: return null;
    }
  }, []);
  const sort = useSort(data.modelos, getVal);

  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button size="sm" onClick={() => setEditando('nueva')}><Plus className="w-4 h-4 mr-1" /> Nuevo modelo</Button></div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Modelos ({data.modelos.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="tipo" sort={sort}>Tipo</SortTh>
              <SortTh col="marca" sort={sort}>Marca</SortTh>
              <SortTh col="modelo" sort={sort}>Modelo</SortTh>
              <SortTh col="revision" sort={sort}>Revisión</SortTh>
              <SortTh col="activo" sort={sort}>Activo</SortTh>
              <th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin modelos registrados</td></tr>}
              {sort.sorted.map((m, i) => (
                <tr key={m.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{tipoLabel(m.tipo)}</td>
                  <td className="py-2 pr-3">{m.marca}</td>
                  <td className="py-2 pr-3">{m.modelo}</td>
                  <td className="py-2 pr-3">{m.revision ?? '—'}</td>
                  <td className="py-2 pr-3">{m.activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right">
                    <button type="button" className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEditando(m)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {editando && <PanelModelo modelo={editando === 'nueva' ? null : editando} guardar={guardar} onClose={() => setEditando(null)} />}
    </div>
  );
}

function PanelModelo({ modelo, guardar, onClose }: { modelo: Catalogo['modelos'][number] | null; guardar: GuardarMutation; onClose: () => void }) {
  const [tipo, setTipo] = useState(modelo?.tipo ?? TIPOS_EQUIPO[0][0]);
  const [marca, setMarca] = useState(modelo?.marca ?? '');
  const [modeloNombre, setModeloNombre] = useState(modelo?.modelo ?? '');
  const [revision, setRevision] = useState(modelo?.revision ?? '');
  const [activo, setActivo] = useState(modelo?.activo ?? true);

  const valido = marca.trim().length > 0 && modeloNombre.trim().length > 0;
  const onGuardar = () => {
    guardar.mutate({
      tabla: 'modelo',
      d: { id: modelo?.id, tipo, marca: marca.trim(), modelo: modeloNombre.trim(), revision: revision.trim() || null, activo },
    }, { onSuccess: onClose });
  };

  return (
    <PanelIntegrado title={modelo ? `Editar modelo · ${modelo.marca} ${modelo.modelo}` : 'Nuevo modelo'} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <Campo label="Tipo de equipo *">
          <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {TIPOS_EQUIPO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Campo>
        <Campo label="Marca *"><input className={fieldCls} value={marca} onChange={(e) => setMarca(e.target.value)} placeholder="Ej. Sony" /></Campo>
        <Campo label="Modelo *"><input className={fieldCls} value={modeloNombre} onChange={(e) => setModeloNombre(e.target.value)} placeholder="Ej. PlayStation 5" /></Campo>
        <Campo label="Revisión / variante"><input className={fieldCls} value={revision} onChange={(e) => setRevision(e.target.value)} placeholder="Ej. Slim, Disc, Digital" /></Campo>
        <Switch checked={activo} onChange={setActivo} label="Modelo activo" />
      </div>
      <div className="flex justify-end gap-2 border-t pt-3">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button isLoading={guardar.isPending} disabled={!valido} onClick={onGuardar}>Guardar</Button>
      </div>
    </PanelIntegrado>
  );
}

// ───────────────────────────── Técnicos ───────────────────────────────────
function TecnicosTab({ data, guardar }: { data: Catalogo; guardar: GuardarMutation }) {
  const [editando, setEditando] = useState<Catalogo['tecnicos'][number] | 'nuevo' | null>(null);
  const getVal = useCallback((t: Catalogo['tecnicos'][number], key: string): string | number | null => {
    switch (key) {
      case 'nombre': return t.nombre;
      case 'bancos': return t.bancos.join(', ');
      case 'activo': return t.activo ? 1 : 0;
      default: return null;
    }
  }, []);
  const sort = useSort(data.tecnicos, getVal);

  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button size="sm" onClick={() => setEditando('nuevo')}><Plus className="w-4 h-4 mr-1" /> Nuevo técnico</Button></div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Técnicos ({data.tecnicos.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="nombre" sort={sort}>Nombre</SortTh>
              <SortTh col="bancos" sort={sort}>Bancos / habilidades</SortTh>
              <th className="py-2 pr-3">Usuario</th>
              <SortTh col="activo" sort={sort}>Activo</SortTh>
              <th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin técnicos registrados</td></tr>}
              {sort.sorted.map((t, i) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{t.nombre}</td>
                  <td className="py-2 pr-3">
                    {t.bancos.length === 0 ? '—' : (
                      <div className="flex flex-wrap gap-1">
                        {t.bancos.map((b) => <span key={b} className="rounded bg-muted px-1.5 py-0.5 text-xs">{BANCOS.find(([v]) => v === b)?.[1] ?? b}</span>)}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3">{t.usuario ?? <span className="text-muted-foreground">Sin vincular</span>}</td>
                  <td className="py-2 pr-3">{t.activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right">
                    <button type="button" className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEditando(t)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {editando && <PanelTecnico tecnico={editando === 'nuevo' ? null : editando} usuarios={data.usuarios} guardar={guardar} onClose={() => setEditando(null)} />}
    </div>
  );
}

function PanelTecnico({ tecnico, usuarios, guardar, onClose }: {
  tecnico: Catalogo['tecnicos'][number] | null; usuarios: Catalogo['usuarios']; guardar: GuardarMutation; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState(tecnico?.nombre ?? '');
  const [bancos, setBancos] = useState<string[]>(tecnico?.bancos ?? []);
  const [activo, setActivo] = useState(tecnico?.activo ?? true);
  const [usuarioId, setUsuarioId] = useState(tecnico?.usuario_id ?? '');

  const vincular = useMutation({
    mutationFn: () => tallerRpc('tecnico_vincular', { _tecnico: tecnico?.id, _usuario: usuarioId || null }),
    onSuccess: () => { toast.success('Usuario vinculado'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const toggleBanco = (b: string) => setBancos((bs) => (bs.includes(b) ? bs.filter((x) => x !== b) : [...bs, b]));
  const valido = nombre.trim().length > 0;
  const onGuardar = () => {
    guardar.mutate({ tabla: 'tecnico', d: { id: tecnico?.id, nombre: nombre.trim(), bancos, activo } }, { onSuccess: onClose });
  };

  return (
    <PanelIntegrado title={tecnico ? `Editar técnico · ${tecnico.nombre}` : 'Nuevo técnico'} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <Campo label="Nombre *"><input className={fieldCls} value={nombre} onChange={(e) => setNombre(e.target.value)} /></Campo>
        <div>
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Bancos / habilidades</span>
          <div className="flex flex-wrap gap-2">
            {BANCOS.map(([v, l]) => (
              <button key={v} type="button" onClick={() => toggleBanco(v)}
                className={`rounded-full border px-3 py-1 text-xs ${bancos.includes(v) ? 'border-primary bg-primary text-primary-foreground' : 'border-input text-muted-foreground hover:bg-muted'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <Switch checked={activo} onChange={setActivo} label="Técnico activo" />
      </div>
      <div className="flex justify-end gap-2 border-t pt-3">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button isLoading={guardar.isPending} disabled={!valido} onClick={onGuardar}>Guardar</Button>
      </div>

      {tecnico && (
        <div className="space-y-2 border-t pt-3 text-sm">
          <p className="text-xs text-muted-foreground">El usuario vinculado ve su cola en «Mi banco» desde el celular.</p>
          <Campo label="Usuario del sistema">
            <select className={fieldCls} value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)}>
              <option value="">— sin vincular —</option>
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
            </select>
          </Campo>
          <div className="flex justify-end">
            <Button size="sm" variant="outline" isLoading={vincular.isPending} onClick={() => vincular.mutate()}>Guardar usuario</Button>
          </div>
        </div>
      )}
    </PanelIntegrado>
  );
}

// ─────────────────────────── Compatibilidad ───────────────────────────────
function CompatibilidadTab({ data }: { data: Catalogo }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [editando, setEditando] = useState<Catalogo['repuestos'][number] | null>(null);

  const guardar = useMutation({
    mutationFn: (p: { producto: string; modelos: string[] }) => tallerRpc('compatibilidad_guardar', { _producto: p.producto, _modelos: p.modelos }),
    onSuccess: () => { toast.success('Compatibilidad guardada'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const modeloNombre = useCallback((id: string) => {
    const m = data.modelos.find((x) => x.id === id);
    return m ? `${m.marca} ${m.modelo}${m.revision ? ` ${m.revision}` : ''}` : '(modelo eliminado)';
  }, [data.modelos]);

  const filtrados = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return data.repuestos.filter((r) => !qq || [r.codigo, r.nombre].some((v) => v.toLowerCase().includes(qq)));
  }, [data.repuestos, q]);

  return (
    <div className="space-y-3">
      <input className={`${fieldCls} w-72`} placeholder="Buscar repuesto por código o nombre…" value={q} onChange={(e) => setQ(e.target.value)} />
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Repuestos ({filtrados.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <th className="py-2 pr-3">Código</th>
              <th className="py-2 pr-3">Repuesto</th>
              <th className="py-2 pr-3">Modelos compatibles</th>
              <th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin repuestos registrados</td></tr>}
              {filtrados.map((r, i) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.codigo}</td>
                  <td className="py-2 pr-3">{r.nombre}</td>
                  <td className="py-2 pr-3 max-w-sm">
                    {r.modelos.length === 0 ? <span className="text-muted-foreground">Sin registrar (compatible con todos)</span> : (
                      <div className="flex flex-wrap gap-1">{r.modelos.map((mid) => <span key={mid} className="rounded bg-muted px-1.5 py-0.5 text-xs">{modeloNombre(mid)}</span>)}</div>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <button type="button" className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEditando(r)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {editando && <PanelCompatibilidad repuesto={editando} modelos={data.modelos} guardar={guardar} onClose={() => setEditando(null)} />}
    </div>
  );
}

function PanelCompatibilidad({ repuesto, modelos, guardar, onClose }: {
  repuesto: Catalogo['repuestos'][number]; modelos: Catalogo['modelos'];
  guardar: { mutate: (p: { producto: string; modelos: string[] }, opts?: { onSuccess?: () => void }) => void; isPending: boolean };
  onClose: () => void;
}) {
  const [sel, setSel] = useState<string[]>(repuesto.modelos);
  const porTipo = useMemo(() => {
    const m = new Map<string, Catalogo['modelos']>();
    for (const [v] of TIPOS_EQUIPO) m.set(v, []);
    modelos.forEach((mo) => m.get(mo.tipo)?.push(mo));
    return m;
  }, [modelos]);
  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const onGuardar = () => guardar.mutate({ producto: repuesto.id, modelos: sel }, { onSuccess: onClose });

  return (
    <PanelIntegrado title={`Compatibilidad · ${repuesto.codigo} · ${repuesto.nombre}`} onClose={onClose} wide>
      <p className="text-xs text-muted-foreground">Marque los modelos con los que este repuesto es compatible. Sin ninguno marcado, se asume compatible con todos (sin restricción).</p>
      <div className="max-h-[50vh] space-y-3 overflow-y-auto">
        {TIPOS_EQUIPO.map(([v, l]) => {
          const ms = porTipo.get(v) ?? [];
          if (ms.length === 0) return null;
          return (
            <div key={v}>
              <h4 className="mb-1 text-xs font-semibold text-muted-foreground">{l}</h4>
              <div className="flex flex-wrap gap-2">
                {ms.map((mo) => (
                  <label key={mo.id}
                    className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${sel.includes(mo.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-input text-muted-foreground hover:bg-muted'}`}>
                    <input type="checkbox" className="hidden" checked={sel.includes(mo.id)} onChange={() => toggle(mo.id)} />
                    {mo.marca} {mo.modelo}{mo.revision ? ` ${mo.revision}` : ''}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
        {modelos.length === 0 && <p className="text-sm text-muted-foreground">No hay modelos registrados todavía.</p>}
      </div>
      <div className="flex justify-end gap-2 border-t pt-3">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button isLoading={guardar.isPending} onClick={onGuardar}>Guardar</Button>
      </div>
    </PanelIntegrado>
  );
}
